/**
 * Kontraktgeschaeft.
 *
 * Die Lieferungs- und Zahlungsbedingungen fuer Muehlenprodukte (VDM)
 * unterscheiden in Abschnitt V nach Lieferzeit:
 *
 *   sofort  — Lieferung bis zum 3. Geschaeftstag
 *   prompt  — Lieferung bis zum 8. Geschaeftstag
 *   termin  — Lieferung auf Termin, ueber einen Monat hinaus. Dann ist die
 *             Menge in monatlich gleichen Raten abzunehmen, Teilpartien
 *             sollen 5 000 kg nicht unterschreiten.
 *
 * Ein Kontraktangebot ist damit etwas anderes als ein Spot-Angebot: Es nennt
 * eine Gesamtmenge ueber einen Zeitraum und einen Abrufrhythmus, und der
 * Preis gilt fuer die Laufzeit.
 */

import { roundDiv } from "./money.js";

export type Vertragsart = "spot" | "kontrakt";
export type Lieferart = "sofort" | "prompt" | "termin";
export type Rhythmus = "monatlich" | "quartalsweise" | "auf_abruf";

export const LIEFERART_TEXT: Record<Lieferart, string> = {
  sofort: "sofortige Lieferung (bis 3. Geschäftstag)",
  prompt: "prompte Lieferung (bis 8. Geschäftstag)",
  termin: "Lieferung auf Termin",
};

export const RHYTHMUS_TEXT: Record<Rhythmus, string> = {
  monatlich: "monatlich gleiche Raten",
  quartalsweise: "quartalsweise",
  auf_abruf: "auf Abruf",
};

/** VDM Abschn. V: Teilpartien sollen 5 000 kg nicht unterschreiten. */
export const TEILPARTIE_MIN_KG = 5000;

/**
 * Obergrenze der Kontraktmenge.
 *
 * Zwei Zwecke, und der zweite ist der wichtigere:
 *
 * 1. Vertipperschutz. Ein Kontrakt ueber 500 000 t ist keiner.
 * 2. Sie haelt das Zwischenprodukt `menge_kg × preis_je_t_e4` klein.
 *
 * Sie ist aber ausdruecklich KEIN Beweis, dass kein Ueberlauf entstehen kann.
 * Der erste Versuch, eine Preisdecke herzuleiten, lag um Faktor 3200 daneben:
 * Gerechnet war mit 78 % Ausbeute und 25 t Lademenge, zulaessig sind aber
 * 100 kg Lademenge (`leseSzenario`) und eine sehr kleine Ausbeute, durch die
 * geteilt wird. Den Beweis fuehrt deshalb nicht diese Konstante, sondern die
 * Pruefung jeder Teilsumme in `gewichteterPreis`.
 */
export const KONTRAKT_MAX_KG = 10_000_000;

export interface AbrufZeile {
  bezeichnung: string;
  menge_kg: number;
  /**
   * Die Kalendermonate, die diese Zeile abdeckt — chronologisch.
   *
   * Warum eine LISTE und nicht ein `(jahr, monat)`-Paar: Eine Quartalszeile
   * deckt ein bis drei Monate ab, und die Dreierbloecke laufen ab dem
   * STARTmonat, nicht an Kalenderquartalsgrenzen. Bei einem Kontrakt
   * September–August ueberspannen drei von vier Quartalen eine
   * Kontraktgrenze — „Juni–August" enthaelt den Mai- UND den
   * Dezemberkontrakt und dazu die Augustgrenze. Ein Paar koennte das nicht
   * darstellen, und ein Quartal einem Kontrakt zuzuschlagen waere bei
   * 1200 t ein fuenfstelliger Fehler.
   *
   * Bei `auf_abruf` FEHLT das Feld: Diese Zeile hat keinen Zeitbezug.
   * Deshalb optional — und deshalb bleiben die Bestandsaufrufer unberuehrt.
   */
  monate?: { jahr: number; monat: number }[];
}

export interface Abrufplan {
  zeilen: AbrufZeile[];
  /** Warnungen, die der Sachbearbeiter sehen soll — kein harter Fehler. */
  hinweise: string[];
}

/** Monate zwischen zwei ISO-Daten, beide einschliesslich. 0 bei Unsinn. */
export function monateZwischen(von: string, bis: string): number {
  const a = /^(\d{4})-(\d{2})/.exec(von);
  const b = /^(\d{4})-(\d{2})/.exec(bis);
  if (!a || !b) return 0;

  const n =
    (Number(b[1]) - Number(a[1])) * 12 + (Number(b[2]) - Number(a[2])) + 1;
  return n > 0 ? n : 0;
}

const MONATSNAMEN = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

/**
 * Verteilt die Kontraktmenge auf die Abrufperioden.
 *
 * Ganzzahlig in Kilogramm: der Rest wird auf die ersten Perioden verteilt,
 * damit die Summe der Raten exakt der Kontraktmenge entspricht. "Monatlich
 * gleiche Raten" mit einem Rundungsverlust von ein paar Kilo waere sonst
 * genau die Art Differenz, ueber die spaeter diskutiert wird.
 */
export function berechneAbrufplan(
  von: string,
  bis: string,
  gesamtKg: number,
  rhythmus: Rhythmus,
): Abrufplan {
  const hinweise: string[] = [];

  if (!Number.isInteger(gesamtKg) || gesamtKg <= 0)
    return { zeilen: [], hinweise: ["Keine Kontraktmenge angegeben."] };

  if (rhythmus === "auf_abruf")
    return {
      zeilen: [{ bezeichnung: "auf Abruf", menge_kg: gesamtKg }],
      hinweise,
    };

  const monate = monateZwischen(von, bis);
  if (!monate)
    return { zeilen: [], hinweise: ["Kontraktzeitraum ist unvollständig oder verdreht."] };
  if (monate > 60)
    return { zeilen: [], hinweise: ["Kontraktzeitraum überschreitet 60 Monate."] };

  const jahr = Number(von.slice(0, 4));
  const monat = Number(von.slice(5, 7)) - 1;

  /*
   * Perioden mit ihren Monaten. Der Anzeigetext bleibt derselbe wie bisher;
   * neu ist, dass jede Zeile weiss, welche Kalendermonate sie abdeckt.
   */
  const perioden: { bezeichnung: string; monate: { jahr: number; monat: number }[] }[] = [];
  const kalender = (m: number) => ({ jahr: jahr + Math.floor(m / 12), monat: (m % 12) + 1 });

  if (rhythmus === "monatlich") {
    for (let i = 0; i < monate; i++) {
      const m = monat + i;
      perioden.push({
        bezeichnung: `${MONATSNAMEN[m % 12]} ${jahr + Math.floor(m / 12)}`,
        monate: [kalender(m)],
      });
    }
  } else {
    // Quartalsweise: angebrochene Quartale zaehlen voll.
    for (let i = 0; i < monate; i += 3) {
      const m = monat + i;
      const letzter = Math.min(i + 2, monate - 1) + monat;
      const eigene: { jahr: number; monat: number }[] = [];
      for (let x = m; x <= letzter; x++) eigene.push(kalender(x));
      /*
       * Ueberspannt das Quartal den Jahreswechsel, stehen BEIDE Jahre da.
       *
       * Vorher nannte das Etikett nur das Jahr des ersten Monats:
       * „Dezember–Februar 2026" — der Februar ist aber 2027. Das war nicht
       * bloss intern unschoen, es steht in der Abrufplantabelle des
       * Angebots-PDF und damit auf Papier beim Kunden. Betroffen war jedes
       * Quartal, das im Oktober, November oder Dezember beginnt.
       *
       * Jahr und Monat trotzdem nie aus der Bezeichnung zurueckrechnen —
       * dafuer gibt es `monate`.
       */
      const jahrVon = jahr + Math.floor(m / 12);
      const jahrBis = jahr + Math.floor(letzter / 12);
      perioden.push({
        bezeichnung:
          jahrVon === jahrBis
            ? `${MONATSNAMEN[m % 12]}–${MONATSNAMEN[letzter % 12]} ${jahrVon}`
            : `${MONATSNAMEN[m % 12]} ${jahrVon}–${MONATSNAMEN[letzter % 12]} ${jahrBis}`,
        monate: eigene,
      });
    }
  }

  /*
   * Verteilt wird JE MONAT, nicht je Periode.
   *
   * Vorher wurde die Menge durch die Zahl der Perioden geteilt. Der grobe
   * Fehler entstand nur bei einer angebrochenen Endperiode — sie bekam
   * dieselbe Rate wie ein volles Quartal:
   *
   *   400 t auf vier Monate, quartalsweise
   *     vorher:  Okt–Dez 200 t (66,7 t/Monat) · Januar 200 t (200 t/Monat)
   *     jetzt:   Okt–Dez 300 t (100 t/Monat)  · Januar 100 t (100 t/Monat)
   *
   * Der Januar allein bekam so viel wie die drei Monate davor zusammen. Die
   * Lieferungs- und Zahlungsbedingungen fuer Muehlenprodukte (Abschn. V)
   * verlangen „monatlich gleiche Raten"; das war keine.
   *
   * Seit dem Kontraktmodus wiegt dieselbe Aufteilung auch den Preis — die
   * Monate ziehen verschiedene MATIF-Kontrakte, eine falsche Menge
   * verschiebt also den Kontraktpreis (gemessen 1,12 €/t bei fuenf Monaten).
   *
   * Der Rest geht auf die ersten MONATE, damit die Summe exakt bleibt.
   * Vorher ging er auf die ersten PERIODEN; auch bei glatter Laufzeit
   * verschieben sich dadurch einzelne Raten um ein bis zwei Kilogramm
   * (gemessen in 48 von 144 Faellen). Auf den Preis wirkt das in der
   * sechsten Nachkommastelle — genannt, weil der naechste Leser sonst
   * annimmt, glatte Laufzeiten seien voellig unberuehrt.
   *
   * Weil die Perioden zusammenhaengend und der Reihe nach liegen, kommt
   * `verteileAufMonate` beim erneuten Aufteilen einer Periodenmenge auf
   * dieselben Monatswerte — ein Test haelt das fest.
   */
  const monateGesamt = perioden.reduce((s, p) => s + p.monate.length, 0);
  const jeMonat = Math.floor(gesamtKg / monateGesamt);
  const restMonate = gesamtKg - jeMonat * monateGesamt;

  let lfd = 0;
  const zeilen: AbrufZeile[] = perioden.map((p) => {
    let menge = 0;
    for (let i = 0; i < p.monate.length; i++) menge += jeMonat + (lfd++ < restMonate ? 1 : 0);
    return { bezeichnung: p.bezeichnung, menge_kg: menge, monate: p.monate };
  });

  /*
   * Verglichen wird die PERIODENmenge, nicht die Monatsrate — und das bleibt
   * so, auch wenn die Warnung durch die neue Verteilung oefter anschlaegt.
   *
   * Eine Teilpartie ist eine Lieferung. Bei quartalsweisem Abruf wird einmal
   * je Quartal geliefert, die Periodenmenge IST also die Teilpartie. Dass
   * eine angebrochene Endperiode jetzt weniger bekommt und damit unter die
   * Schwelle rutschen kann, ist kein Nebeneffekt, sondern die richtige
   * Auskunft: Diese eine Lieferung ist wirklich zu klein.
   */
  const kleinste = Math.min(...zeilen.map((z) => z.menge_kg));
  if (kleinste < TEILPARTIE_MIN_KG)
    hinweise.push(
      `Die Rate liegt bei ${(kleinste / 1000).toLocaleString("de-DE")} t. ` +
        `Teilpartien sollen ${TEILPARTIE_MIN_KG / 1000} t nicht unterschreiten ` +
        `(Lieferungs- und Zahlungsbedingungen für Mühlenprodukte, Abschn. V).`,
    );

  return { zeilen, hinweise };
}

/**
 * Bei einem Terminkontrakt ist die Bindefrist die falsche Uhr: Nicht das
 * Angebot laeuft ab, sondern der Preis gilt fuer die Kontraktlaufzeit.
 * Diese Pruefung faengt den haeufigsten Eingabefehler ab.
 */
export function pruefeKontrakt(
  von: string,
  bis: string,
  gesamtKg: number,
  angebotsdatum: string,
): string | null {
  if (!von || !bis) return "Kontraktzeitraum angeben.";
  if (bis < von) return "Das Kontraktende liegt vor dem Beginn.";
  if (!Number.isInteger(gesamtKg) || gesamtKg <= 0) return "Kontraktmenge angeben.";
  if (gesamtKg > KONTRAKT_MAX_KG)
    return `Kontraktmenge über ${KONTRAKT_MAX_KG / 1000} t — bitte aufteilen.`;
  if (bis < angebotsdatum) return "Der Kontraktzeitraum liegt vollständig in der Vergangenheit.";
  return null;
}

/* ── Kontraktpreis ──────────────────────────────────────────────────────── */

/** Eine Monatsscheibe der Laufzeit. */
export interface Monatsscheibe {
  jahr: number;
  monat: number;
  menge_kg: number;
}

/**
 * Loest den Abrufplan in Monatsscheiben auf.
 *
 * GERECHNET WIRD IMMER JE MONAT, nie je Rhythmusperiode.
 *
 * Eine Quartalszeile darf nicht einem Kontrakt zugeschlagen werden: Die
 * Dreierbloecke laufen ab dem STARTmonat, nicht an Kalenderquartalsgrenzen.
 * Bei einem Kontrakt September–August ueberspannen drei von vier Quartalen
 * eine Kontraktgrenze; „Juni–August" enthaelt den Mai- UND den
 * Dezemberkontrakt und dazu die Augustgrenze. Wuerde das Quartal einem
 * Kontrakt zugeschlagen, bekaemen 300 t den Abschlag statt 100 t — bei
 * zulaessigen ±100 €/t Abschlag ein fuenfstelliger Fehler.
 *
 * Die Rhythmuszeile ist reine Darstellung; ihre Rate wird auf ihre Monate
 * verteilt. Rest auf die ersten Monate, damit die Summe exakt bleibt.
 */
export function verteileAufMonate(zeilen: AbrufZeile[]): Monatsscheibe[] {
  const raus: Monatsscheibe[] = [];

  for (const z of zeilen) {
    const monate = z.monate ?? [];
    if (!monate.length) continue;

    const basis = Math.floor(z.menge_kg / monate.length);
    const rest = z.menge_kg - basis * monate.length;
    monate.forEach((m, i) =>
      raus.push({ jahr: m.jahr, monat: m.monat, menge_kg: basis + (i < rest ? 1 : 0) }),
    );
  }

  return raus;
}

/**
 * Mengengewichteter Preis ueber die Laufzeit.
 *
 *   Kontraktpreis = Σ(menge_kg × preis_je_t) / Σ menge_kg
 *
 * Einmal am Ende gerundet. Die Monatspreise sind bereits gerundet — das ist
 * richtig so, sie sind selbst Ergebnisse und werden auch einzeln angezeigt.
 *
 * `null` heisst „nicht berechenbar", nie 0: Eine Null saehe aus wie ein
 * Ergebnis.
 *
 * ZUM UEBERLAUF. Der Zaehler waechst nicht mit der Zahl der Perioden, weil
 * die Mengen die Gesamtmenge aufteilen — aber die Preisdecke laesst sich
 * nicht aus dem Kopf abschaetzen, und der Versuch ging schon einmal um drei
 * Groessenordnungen daneben: Nicht nur die Notierungsgrenzen bestimmen sie,
 * sondern auch die Lademenge (ab 100 kg zulaessig) und die Ausbeute, durch
 * die geteilt wird. Statt einer Annahme steht hier deshalb die Messung: Jede
 * Teilsumme wird geprueft, und was `MAX_SAFE_INTEGER` reisst, kommt gar nicht
 * erst als Zahl heraus.
 */
export function gewichteterPreis(
  posten: { menge_kg: number; preis_je_t_e4: number }[],
): number | null {
  let zaehler = 0;
  let menge = 0;
  for (const p of posten) {
    const teil = p.menge_kg * p.preis_je_t_e4;
    if (!Number.isSafeInteger(teil) || !Number.isSafeInteger(zaehler + teil)) return null;
    zaehler += teil;
    menge += p.menge_kg;
  }
  return menge > 0 ? roundDiv(zaehler, menge) : null;
}
